import { type AppointmentItem, type ExternalEvent, TASK_PRIORITIES, type TaskItem, type TaskPriority } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useUndoWindow } from "./undo";
import { del, errorMessage, get, patch, post } from "../api";
import { useUsers } from "../lookups";
import { useSession } from "../session";
import { AlertPicker } from "./alert-picker";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatDateTime } from "./ui";
import { RichText } from "./rich-text";


// ── Dates (the browser's time zone, which is the company's for everyone using the app) ──

/** "2026-10-21T09:00" for a datetime-local input. */
export function toLocalInput(iso: string | null | Date) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export const toDateInput = (iso: string | null | Date) => toLocalInput(iso).slice(0, 10);
export const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "Today", "Tomorrow", "Yesterday", or "Mon, Oct 21", plus the time when there is one. */
export function friendlyDue(iso: string) {
  const d = new Date(iso);
  const days = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86_400_000);
  const day = days === 0 ? "Today" : days === 1 ? "Tomorrow" : days === -1 ? "Yesterday" : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = d.getHours() === 12 && d.getMinutes() === 0 ? "" : ` ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  return day + time;
}

const PRIORITY_LABEL: Record<TaskPriority, string> = { high: "High", medium: "Medium", low: "Low" };
const PRIORITY_TONE: Record<TaskPriority, "brand" | "wait" | "off"> = { high: "brand", medium: "wait", low: "off" };

/** "open 3 days" */
function aging(createdAt: string) {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  return days < 1 ? "new today" : `open ${days} day${days === 1 ? "" : "s"}`;
}

// ── Tasks ────────────────────────────────────────────────────────────────

/** A list of tasks with a checkbox to complete each, sorted High/Medium/Low then due date (the API's order). */
export function TaskRows({ tasks, empty, showAssignee, showLink = true, onChanged }: { tasks: TaskItem[]; empty?: string; showAssignee?: boolean; showLink?: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState<TaskItem | null>(null);
  const undo = useUndoWindow<TaskItem>();
  const toggle = useMutation({
    mutationFn: ({ t, done }: { t: TaskItem; done: boolean }) => patch(`/api/tasks/${t.id}`, { done }),
    onSuccess: onChanged,
    // Didn't save: put it back as it was.
    onError: (_e, { t }) => undo.release(t.id),
  });
  const complete = (t: TaskItem, index: number) => {
    // Saved now; it stays in the list, crossed out, with Undo for a few seconds.
    undo.hold({ ...t, completedAt: new Date().toISOString() }, index);
    toggle.mutate({ t, done: true });
  };
  const reopen = (t: TaskItem) => {
    undo.release(t.id);
    toggle.mutate({ t, done: false });
  };
  // The list as shown: a task just checked off stays until its Undo time is up (even if it was the last one).
  const shown = undo.merge(tasks);
  if (!shown.length && empty) return <Empty>{empty}</Empty>;
  return (
    <>
      <ul>
        {shown.map((t, index) => {
          const overdue = !t.completedAt && t.dueAt && new Date(t.dueAt) < new Date();
          return (
            <li key={t.id} className="flex items-start gap-3 border-b border-mist px-5 py-3 last:border-0">
              <input
                type="checkbox"
                checked={!!t.completedAt}
                onChange={() => (t.completedAt ? reopen(t) : complete(t, index))}
                aria-label={t.completedAt ? `Reopen ${t.title}` : `Complete ${t.title}`}
                className="mt-1 size-4 shrink-0 accent-[var(--color-brand)]"
              />
              <div className="min-w-0 flex-1">
                <button className={cx("text-left font-semibold hover:text-brand", t.completedAt && "text-muted line-through")} onClick={() => setEditing(t)}>
                  {t.title}
                </button>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                  {!t.completedAt && <Badge tone={PRIORITY_TONE[t.priority]}>{PRIORITY_LABEL[t.priority]}</Badge>}
                  {t.dueAt && <span className={cx(overdue && "font-semibold text-danger")}>{overdue ? "Overdue: " : "Due "}{friendlyDue(t.dueAt)}</span>}
                  {t.remindAt && !t.completedAt && new Date(t.remindAt) > new Date() && <span>Reminder {friendlyDue(t.remindAt)}</span>}
                  {showAssignee && <span>{[t.assignee, ...t.alsoAssigned].map((u) => u.name).join(", ")}</span>}
                  {!showAssignee && t.alsoAssigned.length > 0 && <span>with {t.alsoAssigned.map((u) => u.name).join(", ")}</span>}
                  {showLink && t.jobId && (
                    <Link to={`/jobs/${t.jobId}`} className="font-medium text-brand hover:underline">
                      Job #{t.jobNumber} {t.contactName}
                    </Link>
                  )}
                  {showLink && !t.jobId && t.contactId && (
                    <Link to={`/contacts/${t.contactId}`} className="font-medium text-brand hover:underline">
                      {t.contactName}
                    </Link>
                  )}
                  {undo.isHeld(t.id) ? (
                    <span>
                      Done ·{" "}
                      <button className="font-semibold text-brand hover:underline" onClick={() => reopen(t)}>
                        Undo
                      </button>
                    </span>
                  ) : (
                    <span>{t.completedAt ? `done ${formatDateTime(t.completedAt).toLowerCase()}` : aging(t.createdAt)}</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {editing && <TaskDialog task={editing} onClose={() => setEditing(null)} onSaved={onChanged} />}
    </>
  );
}

/** People as chips to tick on and off (who's going, also assigned, other reps). */
export function PeopleChips({ value, onChange, exclude, legend, hint }: { value: string[]; onChange: (ids: string[]) => void; exclude?: string | null; legend: string; hint?: string }) {
  const users = useUsers();
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-semibold text-ink-soft">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">
        {users.data
          ?.filter((u) => u.id !== exclude && (u.active || value.includes(u.id)))
          .map((u) => {
            const on = value.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(on ? value.filter((x) => x !== u.id) : [...value, u.id])}
                className={cx("flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium", on ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}
              >
                <span className="size-2 rounded-full" style={{ background: u.color }} />
                {u.name}
              </button>
            );
          })}
      </div>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </fieldset>
  );
}

/** Add or edit a task. People without "manage everyone's tasks" can only assign to themselves. */
export function TaskDialog({ task, link, onClose, onSaved }: { task?: TaskItem; link?: { jobId?: string; contactId?: string }; onClose: () => void; onSaved: () => void }) {
  const { me, can } = useSession();
  const users = useUsers();
  const [title, setTitle] = useState(task?.title ?? "");
  const [notes, setNotes] = useState(task?.notes ?? "");
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? "medium");
  const [due, setDue] = useState(toLocalInput(task?.dueAt ?? null));
  const [remind, setRemind] = useState(toLocalInput(task?.remindAt ?? null));
  const [assigneeId, setAssignee] = useState(task?.assignee.id ?? me?.user.id ?? "");
  const [also, setAlso] = useState<string[]>(task?.alsoAssigned.map((u) => u.id) ?? []);
  const manageAll = can("tasks.manage_all");
  const save = useMutation({
    mutationFn: () => {
      const body = { title, notes, priority, dueAt: fromLocalInput(due), remindAt: fromLocalInput(remind), assigneeId, alsoAssigneeIds: also.filter((x) => x !== assigneeId) };
      return task ? patch(`/api/tasks/${task.id}`, body) : post("/api/tasks", { ...body, jobId: link?.jobId ?? null, contactId: link?.contactId ?? null });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => del(`/api/tasks/${task!.id}`),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog
      title={task ? "Edit task" : "New task"}
      onClose={onClose}
      footer={
        <>
          {task && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => window.confirm("Delete this task?") && remove.mutate()}>
              Delete
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="task-form" busy={save.isPending}>
            {task ? "Save" : "Add task"}
          </Button>
        </>
      }
    >
      <form
        id="task-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Field label="What needs doing" required value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </SelectField>
          <Field label="Due (optional)" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
        <Field
          label="Remind (optional)"
          type="datetime-local"
          value={remind}
          onChange={(e) => setRemind(e.target.value)}
          hint="A push notification at this time, to whoever it's for. Turn them on under My profile (click your name at the bottom left)."
        />
        {manageAll && (
          <SelectField label="For" value={assigneeId} onChange={(e) => setAssignee(e.target.value)}>
            {users.data
              ?.filter((u) => u.active || u.id === assigneeId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.id === me?.user.id ? `${u.name} (me)` : u.name}
                </option>
              ))}
          </SelectField>
        )}
        {manageAll ? (
          <PeopleChips legend="Also assigned (optional)" value={also} onChange={setAlso} exclude={assigneeId} hint="They see it on their lists and get the reminder too. Anyone on it can mark it done." />
        ) : (
          task?.alsoAssigned.length ? <p className="text-sm text-muted">Also assigned: {task.alsoAssigned.map((u) => u.name).join(", ")}</p> : null
        )}
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </form>
    </Dialog>
  );
}

/** Tasks on a job or contact, with an Add button. */
export function TasksPanel({ jobId, contactId }: { jobId?: string; contactId?: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const qs = new URLSearchParams({ status: showDone ? "all" : "open", ...(jobId ? { jobId } : {}), ...(contactId ? { contactId } : {}) });
  const queryKey = ["tasks", jobId ?? contactId, showDone];
  const tasks = useQuery({ queryKey, queryFn: () => get<TaskItem[]>(`/api/tasks?${qs}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  return (
    <Panel>
      <PanelHeader
        title="Tasks"
        action={
          <Button className="px-3 py-1.5" onClick={() => setAdding(true)}>
            Add
          </Button>
        }
      />
      {tasks.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : (
        <TaskRows tasks={tasks.data ?? []} empty={showDone ? "No tasks." : "No open tasks."} showAssignee showLink={!jobId} onChanged={refresh} />
      )}
      <div className="border-t border-mist px-5 py-2">
        <button className="text-sm text-muted hover:text-ink" onClick={() => setShowDone(!showDone)}>
          {showDone ? "Hide completed" : "Show completed"}
        </button>
      </div>
      {adding && <TaskDialog link={{ jobId, contactId }} onClose={() => setAdding(false)} onSaved={refresh} />}
    </Panel>
  );
}

// ── Appointments ─────────────────────────────────────────────────────────

export function appointmentWhen(a: Pick<AppointmentItem, "startsAt" | "endsAt" | "allDay">) {
  const s = new Date(a.startsAt);
  const e = new Date(a.endsAt);
  const day = s.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (a.allDay) return toDateInput(s) === toDateInput(e) ? `${day}, all day` : `${day} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, all day`;
  const t = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day}, ${t(s)} – ${t(e)}`;
}

/** Add or edit an appointment. New ones on a job default to the job's address (the API fills it in). */
export function AppointmentDialog({
  appointment,
  link,
  start,
  onClose,
  onSaved,
}: {
  appointment?: AppointmentItem;
  link?: { jobId?: string; contactId?: string; label?: string };
  /** For a new one: when it starts (e.g. the slot clicked on the calendar). */
  start?: Date;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { me, can } = useSession();
  const users = useUsers();
  const editable = can("appointments.edit");
  const defaultStart = start ?? (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  })();
  const [title, setTitle] = useState(appointment?.title ?? "");
  const [type, setType] = useState(appointment?.type ?? me?.lists.appointmentTypes[0] ?? "");
  const [allDay, setAllDay] = useState(appointment?.allDay ?? false);
  const [startsAt, setStartsAt] = useState(toLocalInput(appointment?.startsAt ?? defaultStart));
  const [endsAt, setEndsAt] = useState(toLocalInput(appointment?.endsAt ?? new Date(defaultStart.getTime() + 60 * 60_000)));
  const [location, setLocation] = useState(appointment?.location ?? "");
  const [notes, setNotes] = useState(appointment?.notes ?? "");
  const [attendees, setAttendees] = useState<string[]>(appointment?.attendees.map((a) => a.id) ?? (me ? [me.user.id] : []));
  // Its own alert times; null: its type's or the company's (Settings → Calendar and reminders).
  const [alerts, setAlerts] = useState<number[] | null>(appointment?.alerts ?? null);
  const [free, setFree] = useState(appointment ? !appointment.busy : false);

  const save = useMutation({
    mutationFn: () => {
      // All-day: from the start of the first day to the start of the last (the API stores the days).
      const s = allDay ? new Date(`${startsAt.slice(0, 10)}T00:00`).toISOString() : fromLocalInput(startsAt);
      const e = allDay ? new Date(`${(endsAt || startsAt).slice(0, 10)}T00:00`).toISOString() : fromLocalInput(endsAt);
      const body = { title: title || type || "Appointment", type, allDay, startsAt: s, endsAt: e, location, notes, attendeeIds: attendees, alerts, busy: !free };
      return appointment ? patch(`/api/appointments/${appointment.id}`, body) : post("/api/appointments", { ...body, jobId: link?.jobId ?? null, contactId: link?.contactId ?? null });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => del(`/api/appointments/${appointment!.id}`),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  if (appointment && !editable) {
    return (
      <Dialog title={appointment.title} onClose={onClose}>
        <AppointmentSummary a={appointment} />
      </Dialog>
    );
  }

  return (
    <Dialog
      wide
      title={appointment ? "Edit appointment" : link?.label ? `New appointment: ${link.label}` : "New appointment"}
      onClose={onClose}
      footer={
        <>
          {appointment && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => window.confirm("Cancel this appointment? It comes off everyone's calendar.") && remove.mutate()}>
              Cancel appointment
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" type="submit" form="appt-form" busy={save.isPending} disabled={!attendees.length}>
            {appointment ? "Save" : "Schedule"}
          </Button>
        </>
      }
    >
      <form
        id="appt-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Conflicts attendeeIds={attendees} allDay={allDay} startsAt={startsAt} endsAt={endsAt} />
        {appointment && (appointment.jobId || appointment.contactId) && (
          <div className="text-sm">
            For{" "}
            <Link to={appointment.jobId ? `/jobs/${appointment.jobId}` : `/contacts/${appointment.contactId}`} className="font-semibold text-brand hover:underline" onClick={onClose}>
              {appointment.jobId ? `Job #${appointment.jobNumber} ` : ""}
              {appointment.contactName}
            </Link>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
          <Field label="Title" placeholder={type || "Inspection"} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!appointment} />
          <SelectField label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">None</option>
            {[...new Set([...(me?.lists.appointmentTypes ?? []), type].filter(Boolean))].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </SelectField>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex w-fit items-center gap-2 text-sm font-semibold text-ink-soft">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="accent-[var(--color-brand)]" />
            All day
          </label>
          <label className="flex w-fit items-center gap-2 text-sm font-semibold text-ink-soft" title="On the calendar, but the time stays open (Google Calendar shows it as free).">
            <input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} className="accent-[var(--color-brand)]" />
            Show as free
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {allDay ? (
            <>
              <Field label="From" type="date" value={startsAt.slice(0, 10)} onChange={(e) => setStartsAt(`${e.target.value}T00:00`)} required />
              <Field label="Through" type="date" value={endsAt.slice(0, 10)} onChange={(e) => setEndsAt(`${e.target.value}T00:00`)} required />
            </>
          ) : (
            <>
              <Field
                label="Starts"
                type="datetime-local"
                value={startsAt}
                required
                onChange={(e) => {
                  // Keep the same length when the start moves.
                  const len = new Date(endsAt).getTime() - new Date(startsAt).getTime();
                  setStartsAt(e.target.value);
                  if (e.target.value && len >= 0) setEndsAt(toLocalInput(new Date(new Date(e.target.value).getTime() + len)));
                }}
              />
              <Field label="Ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
            </>
          )}
        </div>
        <Field label="Where" placeholder={link?.jobId ? "The job's address" : ""} value={location} onChange={(e) => setLocation(e.target.value)} />
        <PeopleChips legend="Who's going" value={attendees} onChange={setAttendees} />
        <div className="text-sm">
          <label className="flex items-center gap-2 font-semibold text-ink-soft">
            <input type="checkbox" checked={alerts !== null} onChange={(e) => setAlerts(e.target.checked ? (allDay ? [360] : [1440, 60]) : null)} className="accent-[var(--color-brand)]" />
            Its own alerts
          </label>
          {alerts === null ? (
            <p className="mt-1 text-muted">Alerts as usual for {type || "this type"} (Settings → Calendar and reminders).</p>
          ) : (
            <div className="mt-2">
              <AlertPicker value={alerts} onChange={setAlerts} allDay={allDay} />
            </div>
          )}
        </div>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        {appointment && <SyncNote a={appointment} />}
      </form>
    </Dialog>
  );
}

function SyncNote({ a }: { a: AppointmentItem }) {
  const problems = a.attendees.filter((x) => x.googleError);
  const synced = a.attendees.filter((x) => x.googleSynced);
  if (!problems.length && !synced.length) return null;
  return (
    <p className="text-sm text-muted">
      {synced.length > 0 && `On Google Calendar for ${synced.map((x) => x.name).join(", ")}. `}
      {problems.map((p) => `${p.name}'s Google Calendar: ${p.googleError}`).join(" ")}
    </p>
  );
}

export function AppointmentSummary({ a }: { a: AppointmentItem }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      <dt className="text-muted">When</dt>
      <dd>{appointmentWhen(a)}{a.busy ? "" : " · shows as free"}</dd>
      {a.type && (
        <>
          <dt className="text-muted">Type</dt>
          <dd>{a.type}</dd>
        </>
      )}
      {a.location && (
        <>
          <dt className="text-muted">Where</dt>
          <dd>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.location)}`} target="_blank" rel="noreferrer" className="text-brand hover:underline">
              {a.location}
            </a>
          </dd>
        </>
      )}
      <dt className="text-muted">Who</dt>
      <dd>{a.attendees.map((x) => x.name).join(", ")}</dd>
      {(a.jobId || a.contactId) && (
        <>
          <dt className="text-muted">For</dt>
          <dd>
            <Link to={a.jobId ? `/jobs/${a.jobId}` : `/contacts/${a.contactId}`} className="text-brand hover:underline">
              {a.jobId ? `Job #${a.jobNumber} ` : ""}
              {a.contactName}
            </Link>
          </dd>
        </>
      )}
      {a.notes && (
        <>
          <dt className="text-muted">Notes</dt>
          <dd><RichText text={a.notes} /></dd>
        </>
      )}
    </dl>
  );
}

/** A job's appointments, upcoming first, with a Schedule button. */
export function AppointmentsPanel({ jobId, label }: { jobId: string; label: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const list = useQuery({ queryKey: ["appointments", "job", jobId], queryFn: () => get<AppointmentItem[]>(`/api/jobs/${jobId}/appointments`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["appointments"] });
  const now = Date.now();
  const upcoming = (list.data ?? []).filter((a) => new Date(a.endsAt).getTime() >= now);
  const past = (list.data ?? []).filter((a) => new Date(a.endsAt).getTime() < now).reverse();
  return (
    <Panel>
      <PanelHeader
        title="Appointments"
        action={
          can("appointments.edit") && (
            <Button className="px-3 py-1.5" onClick={() => setAdding(true)}>
              Schedule
            </Button>
          )
        }
      />
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.length ? (
        <Empty>Nothing scheduled.</Empty>
      ) : (
        <ul>
          {[...upcoming, ...past].map((a) => (
            <li key={a.id} className={cx("border-b border-mist last:border-0", new Date(a.endsAt).getTime() < now && "opacity-60")}>
              <button className="w-full px-5 py-3 text-left hover:bg-mist/50" onClick={() => setOpen(a)}>
                <div className="font-semibold">{a.title}</div>
                <div className="text-xs text-muted">
                  {appointmentWhen(a)} · {a.attendees.map((x) => x.name).join(", ")}
                </div>
                <WeatherFlag a={a} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {adding && <AppointmentDialog link={{ jobId, label }} onClose={() => setAdding(false)} onSaved={refresh} />}
      {open && <AppointmentDialog appointment={open} onClose={() => setOpen(null)} onSaved={refresh} />}
    </Panel>
  );
}

/** A weather warning on scheduled work (rain or wind over the thresholds in Settings). */
export function WeatherFlag({ a, compact }: { a: AppointmentItem; compact?: boolean }) {
  const w = a.weather;
  if (!w?.reasons.length) return null;
  const text = `${w.reasons.join(", ")} (${w.summary})`;
  return compact ? (
    <span className="shrink-0 font-bold text-danger" title={`Weather: ${text}`} aria-label={`Weather warning: ${text}`}>
      ⚠
    </span>
  ) : (
    <span className="mt-0.5 inline-block rounded bg-danger-wash px-1.5 py-0.5 text-xs font-semibold text-danger">⚠ Weather: {text}</span>
  );
}

/** "Ryan has something then": what's on the people's other calendars at that time (as much as they share). */
function Conflicts({ attendeeIds, allDay, startsAt, endsAt }: { attendeeIds: string[]; allDay: boolean; startsAt: string; endsAt: string }) {
  const from = allDay ? new Date(`${startsAt.slice(0, 10)}T00:00`) : new Date(startsAt);
  const to = allDay ? new Date(new Date(`${(endsAt || startsAt).slice(0, 10)}T00:00`).getTime() + 86_400_000) : new Date(endsAt);
  const ok = attendeeIds.length > 0 && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to > from;
  const qs = ok ? new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), users: attendeeIds.join(",") }).toString() : "";
  const q = useQuery({ queryKey: ["external-events", "conflicts", qs], queryFn: () => get<ExternalEvent[]>(`/api/calendar/external?${qs}`), enabled: ok, staleTime: 60_000 });
  // An all-day event on another calendar doesn't block a timed appointment.
  const hits = (q.data ?? []).filter((e) => allDay || !e.allDay);
  if (!hits.length) return null;
  const when = (e: ExternalEvent) => (e.allDay ? "all day" : `${new Date(e.startsAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}–${new Date(e.endsAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`);
  return (
    <Alert tone="info">
      Heads up:{" "}
      {hits
        .slice(0, 3)
        .map((e) => `${e.mine ? "you have" : `${e.userName.split(" ")[0]} has`} ${e.title === "Busy" ? "something" : `“${e.title}”`} ${when(e)}`)
        .join("; ")}
      {hits.length > 3 ? `, and ${hits.length - 3} more` : ""}. You can still book it.
    </Alert>
  );
}
